import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const DPBarChart = ({
  graphData = [],
  activeTab = "COF",
  yAxisTitle = "Values",
  showAnnotation = false,
}) => {
  const classes = useStyles();
  const [borderRadius, setBorderRadius] = useState(10);
  // const [isStacked, setIsStacked] = useState(false);
  const isStacked = false;
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(5);
      } else if (screenWidth < 1500) {
        setBorderRadius(6);
      } else {
        setBorderRadius(10);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Determine categories and series data based on activeTab
  let categories = [];
  let series = [];

  if (activeTab === "Capacity Utilization") {
    categories = graphData.map((item) => item.X_Axis || "Unknown Metric");
    series = [
      {
        name: "Current Year Payload",
        data: graphData.map((item) => item.Metric_value || 0),
      },
    ];
    yAxisTitle = "Capacity Utilization %";
  } else {
    // Default case (activeTab === 'COF' or others)
    categories = graphData.map((item) => item.X_Axis || "Unknown");
    series = [
      {
        name: yAxisTitle,
        data: graphData.map((item) => item.COF || 0),
      },
    ];
  }

  // Pagination logic
  const totalItems = graphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const slicedCategories = categories.slice(startIndex, endIndex);
  const slicedSeries = series.map((s) => ({
    ...s,
    data: s.data.slice(startIndex, endIndex),
  }));

  const calculateColumnWidth = () => {
    const categoriesLength = graphData.length;
    if (categoriesLength === 1) {
      return "2%";
    }
    if (categoriesLength === 2) {
      return "5%";
    }
    if (categoriesLength === 3) {
      return "8%";
    }
    return "10%";
  };

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: isStacked,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      animations: {
        enabled: true,
        easing: "easeInOutQuad",
        speed: 600,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    fill: {
      colors: ["#EC6FBD"],
    },
    series: slicedSeries,
    yaxis: {
      labels: {
        formatter: (value) => `${value}%`,
      },
      title: {
        text: yAxisTitle,
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          color: "#333",
        },
      },
      min: activeTab === "Capacity Utilization" ? 0 : 50,
      max: activeTab === "Capacity Utilization" ? 100 : 100,
      tickAmount: 5,
    },
    xaxis: {
      categories: slicedCategories,
      tickAmount: slicedCategories.length,
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        cssClass: "force-xaxis-rotate",
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
      axisBorder: {
        offsetY: activeTab === "COF" ? 3 : 0,
      },
    },
    annotations: {
      yaxis: showAnnotation
        ? [
            {
              y: 98,
              borderColor: "#B0B0B0",
              strokeDashArray: [5, 5],
              label: {
                borderColor: "#B0B0B0",
                style: {
                  color: "#B0B0B0",
                  padding: "5px",
                  fontSize: "10px",
                },
                text: "Target = 98%",
              },
            },
          ]
        : [],
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: calculateColumnWidth(),
        borderRadius: isStacked ? 0 : borderRadius,
        borderColors: ["#000000"],
        borderWidth: 2,
      },
    },
    noData: {
      text: "No Data Available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#404041",
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
    },
  };

  return (
    <div id="chart" style={{ padding: "10px 20px" }}>
      <Chart
        options={options}
        series={options.series}
        type="bar"
        height={300}
      />
      {totalPages > 1 && activeTab === "COF" && (
        <div className={classes.sliderContainer}>
          <div className={classes.sliderWrapper}>
            <div
              className={classes.progressTrack}
              style={{
                width: `${((currentPage + 1) / totalPages) * 100}%`,
                opacity: 0.3,
              }}
            />
            <input
              type="range"
              min="0"
              max={totalPages - 1}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
              className={classes.slider}
            />
          </div>
          <div className={classes.pageIndicator}>
            <span>{currentPage + 1}</span> / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default DPBarChart;
